import { Stack, TypographyVariant } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CRMApiEntityType, UserBalance } from '../../@types/api';
import { getData } from '../../utils/api';
import BettingInfoDisplay from '../atoms/BettingInfoDisplay';
import { QUERY_KEYS } from '../../constants';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';

import { mergeSx } from '../../utils/styles';

const typographyVariantMap: Record<string, TypographyVariant> = {
  normal: 'subtitle1',
  small: 'body2',
};

type Props = {
  userType: CRMApiEntityType;
  userId?: string;
  variant?: 'small' | 'normal';
};

const roundDownToTwoDecimals = (num?: number) => {
  return (Math.floor((num ?? 0) * 100) / 100).toFixed(2);
};

const formatBalance = (balance: number | undefined) => roundDownToTwoDecimals(balance);

const UserBalanceDisplay = ({ userType, userId, variant = 'normal' }: Props) => {
  const { userId: loggedInUserId } = useAuth();

  const { data: balanceData } = useQuery<UserBalance>({
    queryKey: [QUERY_KEYS.userBalance, userId],

    queryFn: () =>
      getData(
        'wallets/user-balances',
        {
          userId: userId ?? loggedInUserId,
          userType,
        },
        'wallet'
      ),
  });

  const hasBonusWallet = userType === 'customer';

  const mainWalletBalance = formatBalance(balanceData?.main?.balance);
  const bonusWalletBalance = formatBalance(balanceData?.bonus?.balance);

  return (
    <Stack
      direction='row'
      spacing={2}
      sx={mergeSx(
        {
          alignItems: 'center',
          flex: 1,
        },
        (theme) => ({ [theme.breakpoints.down('sm')]: { width: '100%' } })
      )}
    >
      <BettingInfoDisplay
        label='Main balance'
        value={mainWalletBalance}
        backgroundColor='#EFFFFA'
        borderColor='#55CDA9'
        variant={typographyVariantMap[variant]}
        currency={balanceData?.main?.currency}
      />
      {hasBonusWallet && (
        <BettingInfoDisplay
          label='Bonus balance'
          value={bonusWalletBalance}
          backgroundColor='background.lightBlue'
          borderColor='#2B7FFE'
          variant={typographyVariantMap[variant]}
          currency={balanceData?.bonus?.currency}
        />
      )}
    </Stack>
  );
};

export default UserBalanceDisplay;
